import { AlertTriangle, CheckCircle2, Clock, PlayCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { breakdownLabel, capaStats, type CapaStat, type CapaStatInput } from '@/lib/capaStats'

/**
 * The four figures at the top of the CAPA page: Open, Started, Closed, Overdue.
 *
 * Each answers a different question about the same list, so they are four boxes
 * rather than one segmented number — and they deliberately do not sum to the
 * total. Started is a subset of Open, and a cancelled CAPA is in none of them
 * (see `capaStats` for why).
 *
 * Every box carries the same three lines so the row can be read across:
 * the headline count, what changed in the last seven days, and how the count
 * splits across the kinds of run the CAPAs sit inside.
 *
 * The counts come from the rows the page has ALREADY loaded, not a separate
 * endpoint — so they always agree with the table underneath, including while a
 * filter is applied. That is the useful behaviour: a reader who filters to one
 * patient wants the boxes to describe what they are looking at.
 */
const TONE: Record<CapaStat['key'], { icon: typeof AlertTriangle; value: string; chip: string }> = {
  open: { icon: AlertTriangle, value: 'text-orange-500', chip: 'bg-orange-50 text-orange-500' },
  started: { icon: PlayCircle, value: 'text-blue-600', chip: 'bg-blue-50 text-blue-500' },
  closed: { icon: CheckCircle2, value: 'text-green-600', chip: 'bg-green-50 text-green-500' },
  overdue: { icon: Clock, value: 'text-red-600', chip: 'bg-red-50 text-red-500' },
}

export function CapaStatCards({
  rows,
  loading = false,
}: {
  rows: CapaStatInput[]
  loading?: boolean
}) {
  // Read once per render so four boxes cannot disagree about when "now" is.
  const stats = capaStats(rows, new Date())

  return (
    <div className='mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4'>
      {stats.map((stat) => (
        <StatCard key={stat.key} stat={stat} loading={loading} />
      ))}
    </div>
  )
}

function StatCard({ stat, loading }: { stat: CapaStat; loading: boolean }) {
  const tone = TONE[stat.key]
  const Icon = tone.icon

  return (
    <div className='rounded-xl border bg-background p-4 shadow-sm'>
      <div className='flex items-start justify-between gap-2'>
        <p className='text-xs font-medium text-muted-foreground'>{stat.label}</p>
        <span className={cn('flex h-7 w-7 items-center justify-center rounded-lg', tone.chip)}>
          <Icon className='h-4 w-4' />
        </span>
      </div>

      <p className='mt-1 flex items-baseline gap-2'>
        <span className={cn('text-2xl font-semibold tabular-nums', loading && 'opacity-40', tone.value)}>
          {stat.count}
        </span>
        {stat.thisWeek !== null && (
          <span className='text-xs text-muted-foreground'>
            {stat.thisWeek} {stat.thisWeekVerb} this week
          </span>
        )}
      </p>

      {/* Always rendered, so the four boxes keep the same height whether or not
          a given one has a breakdown to show. */}
      <p className='mt-1 truncate text-xs text-muted-foreground' title={breakdownLabel(stat.byType)}>
        {breakdownLabel(stat.byType)}
      </p>
    </div>
  )
}
